// src/pages/ServicePages/PremiumRentalsAndLogisticsPage.jsx

import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactWidget from '../../components/ui/ContactWidget';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import ClientLogos from 'pages/homepage/components/ClientLogos';
import QuoteModal from '../../components/ui/QuoteModal.jsx';

// Gallery Image Component
const GalleryImage = ({ src, alt }) => (
  <div className="overflow-hidden rounded-xl group shadow-sm hover:shadow-lg transition-shadow duration-300">
    <img 
      src={src} 
      alt={alt} // SEO: Descriptive alt tag
      className="w-full h-full object-cover aspect-[4/3] group-hover:scale-105 transition-transform duration-500 ease-in-out" 
      loading="lazy" // SEO: Lazy loading improves page speed
    />
  </div>
);

// FAQ Item Component for cleaner structure
const FaqItem = ({ question, children }) => (
  <details className="p-6 rounded-lg bg-white border border-gray-200/80 cursor-pointer group transition-colors hover:bg-gray-50">
    <summary className="font-semibold text-lg flex items-center justify-between">
      {question}
      <Icon name="ChevronDown" className="h-5 w-5 transition-transform duration-300 group-open:rotate-180" />
    </summary>
    <div className="text-gray-600 mt-4 leading-relaxed">
      {children}
    </div>
  </details>
);

const PremiumRentalsAndLogisticsPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  // SEO: Alt tags are more descriptive for better image search ranking.
  const galleryItems = [
    { src: '/gallery/elegant-tent-night.jpg', alt: 'Elegant high-peak tent rental for night event in Nairobi.' },
    { src: '/gallery/luxury-lounge-seating.jpg', alt: 'Chic lounge furniture rental for a VIP event in Kenya.' },
    { src: '/gallery/concert-stage-lighting.jpg', alt: 'Professional stage and lighting equipment rental for a concert.' },
    { src: '/gallery/dining-table-arrangement.jpg', alt: 'Dining table and chair hire for a gala under a marquee tent.' },
    { src: '/gallery/sophisticated-av-setup.jpg', alt: 'Sophisticated AV equipment hire for a conference in Nairobi.' },
    { src: '/gallery/custom-event-flooring.jpg', alt: 'Custom dance floor rental and staging for a gala.' },
    { src: '/gallery/ambient-lighting-decor.jpg', alt: 'Ambient string lighting rentals for a magical atmosphere.' },
    { src: '/gallery/logistics-team-in-action.jpg', alt: 'Luxe & Allure event logistics team setting up a structure in Kenya.' },
  ];

  // SEO: Structured Data (JSON-LD) to help Google understand your service.
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": "Luxe & Allure Premium Event Rentals & Logistics",
    "image": "https://www.luxeandallureevents.co.ke/gallery/tent.jpg", // SEO: Replace with your absolute URL
    "@id": "https://www.luxeandallureevents.co.ke/services/premium-rentals-logistics", // SEO: Replace with your absolute URL
    "url": "https://www.luxeandallureevents.co.ke/services/premium-rentals-logistics", // SEO: Replace with your absolute URL
    "telephone": "+254727937010",
    "priceRange": "$$$$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "16 Nairobi, Muthaiga North, Garden Estate Rd", // SEO: Replace with your address
      "addressLocality": "Nairobi",
      "addressCountry": "KE"
    },
    "description": "Leading event rentals and logistics company in Nairobi, Kenya. We provide premium tents, luxury furniture, and professional A/V and lighting production.",
    "serviceType": "Event Rentals Company",
    "areaServed": {
      "@type": "Country",
      "name": "Kenya"
    },
    "provider": {
        "@type": "Organization",
        "name": "Luxe & Allure"
    }
  };

  return (
    <>
      <div className="min-h-screen bg-slate-50 text-gray-800">
        <Helmet>
          {/* === PRIMARY METADATA (CRUCIAL FOR SEO) === */}
          <title>Premium Event Rentals & Tent Hire in Nairobi, Kenya | Luxe & Allure</title>
          <meta name="description" content="Nairobi's top choice for premium event rentals, tent hire, and logistics. We provide luxury furniture, staging, lighting, and AV equipment for flawless events in Kenya." />
          <link rel="canonical" href="https://www.luxeandallureevents.co.ke/services/premium-rentals-logistics" /> {/* SEO: Replace with your final URL */}
          
          {/* === KEYWORDS (Lower impact, but good practice) === */}
          <meta name="keywords" content="event rentals nairobi, tent rental kenya, luxury furniture hire nairobi, event logistics company, av equipment hire, stage and lighting rental kenya, event equipment hire" />

          {/* === OPEN GRAPH (for Social Media Sharing) === */}
          <meta property="og:title" content="Premium Event Rentals & Tent Hire in Nairobi | Luxe & Allure" />
          <meta property="og:description" content="Discover Nairobi's finest selection of rental equipment, from luxury tents and furniture to professional staging and lighting. Your partner for flawless event production." />
          <meta property="og:image" content="https://www.luxeandallureevents.co.ke/gallery/elegant-tent-night.jpg" /> {/* SEO: Replace with an absolute URL to a compelling image */}
          <meta property="og:url" content="https://www.luxeandallureevents.co.ke/services/premium-rentals-logistics" /> {/* SEO: Replace with your final URL */}
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="Luxe & Allure" />

          {/* === TWITTER CARD (for Twitter Sharing) === */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Premium Event Rentals & Tent Hire in Nairobi | Luxe & Allure" />
          <meta name="twitter:description" content="Discover Nairobi's finest selection of rental equipment, from luxury tents and furniture to professional staging and lighting." />
          <meta name="twitter:image" content="https://www.luxeandallureevents.co.ke/gallery/elegant-tent-night.jpg" /> {/* SEO: Replace with an absolute URL */}

          {/* === STRUCTURED DATA (JSON-LD) === */}
          <script type="application/ld+json">
            {JSON.stringify(schemaData)}
          </script>
        </Helmet>

        <Header />
        <ContactWidget />

        <main className="font-sans">
          {/* --- Hero Section --- */}
          <section className="bg-white">
              <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24">
                  <div className="grid lg:grid-cols-2 gap-12 items-center">
                      <div className="text-center lg:text-left">
                          <Breadcrumb />
                          {/* SEO: H1 is the most important heading, using primary keywords. */}
                          <h1 className="font-serif text-5xl md:text-6xl font-bold leading-tight tracking-tight mt-4">
                              Premium Event Rentals & Logistics in Nairobi
                          </h1>
                          <p className="mt-6 text-lg text-gray-600 max-w-xl mx-auto lg:mx-0">
                              We provide the industry's finest rental equipment and precision logistics, ensuring your vision is executed to perfection, no matter the scale or complexity.
                          </p>
                          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                              <Button 
                                  variant="primary" 
                                  size="lg" 
                                  onClick={() => setIsModalOpen(true)}
                              >
                                  Inquire Now
                              </Button>
                              <Button 
                                  variant="outline" 
                                  size="lg"
                                  onClick={() => document.getElementById('inventory-section')?.scrollIntoView({ behavior: 'smooth' })}
                              >
                                  View Inventory
                                  <Icon name="ArrowDown" className="ml-2 w-4 h-4" />
                              </Button>
                          </div>
                      </div>
                      <div className="h-80 lg:h-full w-full">
                          <img 
                              src="/gallery/tent.jpg" 
                              alt="An elegant high-peak rental tent for a major event in Nairobi." // SEO: Alt tag with keywords.
                              className="w-full h-full object-cover rounded-xl shadow-lg" 
                          />
                      </div>
                  </div>
              </div>
          </section>

          {/* --- Our Philosophy Section --- */}
          <section className="py-20 lg:py-28">
              <div className="container mx-auto px-6 lg:px-8 text-center max-w-4xl">
                   {/* SEO: H2 tags target secondary keywords and user intent. */}
                  <h2 className="font-serif text-4xl font-bold text-gray-900">
                      Kenya's Premier Event Rentals Company
                  </h2>
                  <p className="mt-6 text-lg text-gray-600 leading-relaxed">
                      A truly memorable event is more than just beautiful design; it's a seamless experience built on a framework of absolute reliability. At Luxe & Allure, we are the silent power behind the spectacle. Our commitment is to provide not just equipment, but confidence. With a meticulously maintained inventory of event rentals and a logistics team trained in the art of precision, we are the trusted partner for Nairobi's most discerning clients and planners who demand excellence as the standard.
                  </p>
              </div>
          </section>

          {/* --- Our Process Section --- */}
          <section className="py-20 lg:py-28 bg-white">
              <div className="container mx-auto px-6 lg:px-8">
                  <div className="text-center mb-16">
                      <h2 className="font-serif text-4xl font-bold text-gray-900">A Seamless Rental Experience, End-to-End</h2>
                      <p className="mt-4 text-lg text-gray-600">Our four-step process ensures clarity, precision, and flawless execution for your event rentals.</p>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                      <div className="text-center p-6">
                          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full mb-4">
                            <Icon name="ClipboardList" className="w-8 h-8 text-primary" />
                          </div>
                          <h3 className="font-bold text-xl mb-2">1. Consultation & Planning</h3>
                          <p className="text-gray-600">We begin by understanding your technical and aesthetic needs to create a detailed logistics and rental plan.</p>
                      </div>
                      <div className="text-center p-6">
                          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full mb-4">
                            <Icon name="Truck" className="w-8 h-8 text-primary" />
                          </div>
                          <h3 className="font-bold text-xl mb-2">2. Meticulous Preparation</h3>
                          <p className="text-gray-600">Every piece of equipment is inspected, cleaned, and prepared for safe and timely delivery by our expert team.</p>
                      </div>
                      <div className="text-center p-6">
                          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full mb-4">
                            <Icon name="Wrench" className="w-8 h-8 text-primary" />
                          </div>
                          <h3 className="font-bold text-xl mb-2">3. Professional Execution</h3>
                          <p className="text-gray-600">Our on-site technicians and crew manage the complete setup and breakdown with precision and professionalism.</p>
                      </div>
                      <div className="text-center p-6">
                          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-primary/10 rounded-full mb-4">
                            <Icon name="CheckCircle2" className="w-8 h-8 text-primary" />
                          </div>
                          <h3 className="font-bold text-xl mb-2">4. On-Site Support</h3>
                          <p className="text-gray-600">We offer optional on-site support throughout your event to manage technical aspects and ensure everything runs smoothly.</p>
                      </div>
                  </div>
              </div>
          </section>

          {/* --- Our Comprehensive Inventory Section --- */}
          <section id="inventory-section" className="py-20 lg:py-28">
              <div className="container mx-auto px-6 lg:px-8">
                  <div className="text-center mb-16 max-w-3xl mx-auto">
                      <h2 className="font-serif text-4xl font-bold text-gray-900">An Event Hire Inventory Without Compromise</h2>
                      <p className="mt-4 text-lg text-gray-600">We own and maintain a vast selection of premium equipment to meet the demands of any event in Kenya.</p>
                  </div>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                      <div className="bg-white p-8 rounded-xl shadow-sm">
                          <Icon name="Tent" className="w-10 h-10 text-primary mb-4" />
                          <h4 className="font-bold text-xl mb-2">Tent & Marquee Rentals</h4>
                          <p className="text-gray-600">Luxury high-peak tents, clear-span marquees, custom staging, dance floors, and site infrastructure for hire.</p>
                      </div>
                      <div className="bg-white p-8 rounded-xl shadow-sm">
                          <Icon name="Armchair" className="w-10 h-10 text-primary mb-4" />
                          <h4 className="font-bold text-xl mb-2">Furniture & Decor Hire</h4>
                          <p className="text-gray-600">Exclusive collections of seating, tables, bars, and decorative elements to create bespoke environments.</p>
                      </div>
                      <div className="bg-white p-8 rounded-xl shadow-sm">
                          <Icon name="Lamp" className="w-10 h-10 text-primary mb-4" />
                          <h4 className="font-bold text-xl mb-2">Lighting & AV Rental</h4>
                          <p className="text-gray-600">Sophisticated lighting design, concert-grade audio, LED screens, and all necessary power management.</p>
                      </div>
                  </div>
              </div>
          </section>

          {/* --- Gallery Section --- */}
          <section className="py-20 lg:py-28 bg-white">
            <div className="container mx-auto px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="font-serif text-4xl font-bold text-gray-900">Visualizing Excellence in Event Rentals</h2>
                <p className="mt-4 text-lg text-gray-600">Explore a selection of our premium rental equipment in action at events across Nairobi.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {galleryItems.map((item, index) => (
                  <GalleryImage key={index} src={item.src} alt={item.alt} />
                ))}
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="py-20 lg:py-28">
              <div className="container mx-auto px-6 lg:px-8">
                  <div className="text-center mb-16 max-w-3xl mx-auto">
                      <h2 className="font-serif text-4xl font-bold text-gray-900">Event Rentals & Logistics FAQs</h2>
                      <p className="mt-4 text-lg text-gray-600">Your questions about renting equipment for events in Nairobi, answered.</p>
                  </div>
                  <div className="max-w-4xl mx-auto space-y-4">
                      <FaqItem question="Do you offer delivery and setup for your rental items?">
                          <p>Yes, absolutely. We provide a full-service experience. Our professional logistics team handles the timely delivery, complete setup, and breakdown of all rental equipment. We ensure everything is installed safely, correctly, and on schedule, allowing you to have a hassle-free experience.</p>
                      </FaqItem>
                      <FaqItem question="What types of tents are available for rent in Kenya?">
                          <p>We offer a wide range of high-quality structures to suit any event size and style. Our inventory includes elegant high-peak tents, versatile A-frame marquees, and chic canopies. We can also source custom structures for unique event requirements. All our tents are weatherproof and professionally installed to the highest safety standards.</p>
                      </FaqItem>
                      <FaqItem question="Can I rent items without using your planning services?">
                          <p>Of course. We operate as a standalone rental and logistics provider for a wide range of clients, including independent planners, venues, corporations, and private individuals. You can rely on our team and inventory to support your event, regardless of who is managing the creative aspects.</p>
                      </FaqItem>
                  </div>
              </div>
          </section>
          
          {/* --- Final Call to Action Section --- */}
          <section id="contact-section" className="bg-primary text-white">
            <div className="container mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
              <h2 className="font-serif text-4xl font-bold">
                Let's Build Your Next Event
              </h2>
              <p className="mt-4 max-w-xl mx-auto text-primary/80">
                Contact us today to discuss your event's requirements. Our team is ready to provide a detailed quote and a comprehensive rental and logistics solution tailored to your vision.
              </p>
              <Button 
                variant="secondary" 
                size="xl" 
                className="mt-8"
                onClick={() => setIsModalOpen(true)}
              >
                Request a Catalogue & Quote
                <Icon name="BookOpen" className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </section>

          <ClientLogos />
        </main>
      </div>
      
      {isModalOpen && <QuoteModal onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

export default PremiumRentalsAndLogisticsPage;